import { Card } from 'antd';
import { useCityList } from 'apis/home';
import LOCALS from 'commons/locals';
import { findLabelByValue, OrderDeliveryTypeMap } from 'commons/options';
import { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { OmsOrderDetail } from 'types/oms';
import commonApi from 'apis/common';
import { User } from 'lucide-react';
import CopyButton from 'components/copy-button';
import { Image } from 'antd';

type RecipientInformationProps = {
  omsOrderDetail: OmsOrderDetail;
};

const RecipientInformation = ({
  omsOrderDetail: {
    receiverName,
    receiverPhone,
    receiverPostCode,
    receiverProvince,
    receiverCity,
    receiverDetailAddress,
    deliveryType,
    signUrl,
    pickupTime,
  },
}: RecipientInformationProps) => {
  const [signImageUrl, setSignImageUrl] = useState('');
  useEffect(() => {
    if (signUrl && deliveryType === OrderDeliveryTypeMap.PICKUP) {
      commonApi.getSignedFileUrl(signUrl).then((res) => {
        setSignImageUrl(res.url);
      });
    }
  }, [deliveryType, signUrl]);

  const { countryOptions } = useAppSelector(selectGlobalInfo);
  const { cityList } = useCityList(receiverProvince);

  const targetCity = useMemo(() => {
    return cityList.find((i) => i.code === receiverCity);
  }, [cityList, receiverCity]);

  const recipientInformationText = useMemo(() => {
    return `${receiverName} ${
      receiverPhone.startsWith('+') ? receiverPhone : `+${receiverPhone}`
    } ${receiverPostCode} ${receiverDetailAddress} ${
      targetCity ? targetCity.name : receiverCity
    } ${findLabelByValue(receiverProvince, countryOptions)}`;
  }, [
    countryOptions,
    receiverCity,
    receiverDetailAddress,
    receiverName,
    receiverPhone,
    receiverPostCode,
    receiverProvince,
    targetCity,
  ]);

  const infoList = useMemo(() => {
    const list: {
      key: string;
      label: React.ReactNode;
      node: React.ReactNode;
    }[] = [
      {
        key: 'name',
        label: <Trans i18nKey={LOCALS.name} />,
        node: receiverName || '-',
      },
      {
        key: 'phone_number',
        label: <Trans i18nKey={LOCALS.phone_number} />,
        node: receiverPhone || '-',
      },
      {
        key: 'zip_code',
        label: <Trans i18nKey={LOCALS.zip_code} />,
        node: receiverPostCode || '-',
      },
      {
        key: 'country_region',
        label: <Trans i18nKey={LOCALS.country_region} />,
        node: findLabelByValue(receiverProvince, countryOptions),
      },
      {
        key: 'state_province_city',
        label: <Trans i18nKey={LOCALS.state_province_city} />,
        node: targetCity ? targetCity.name : receiverCity || '-',
      },
      {
        key: 'detail_address',
        label: <Trans i18nKey={LOCALS.detail_address} />,
        node: receiverDetailAddress || '-',
      },
    ];

    if (deliveryType === OrderDeliveryTypeMap.PICKUP && !!pickupTime) {
      list.push({
        key: 'pickup_time',
        label: <Trans i18nKey={LOCALS.EsqMNViBHq} />,
        node: pickupTime,
      });
    }

    if (deliveryType === OrderDeliveryTypeMap.PICKUP && !!signImageUrl) {
      list.push({
        key: 'sign_image_url',
        label: <Trans i18nKey={LOCALS.LSyPLaaZjt} />,
        node: <Image src={signImageUrl} width={48} />,
      });
    }

    return list;
  }, [
    receiverName,
    receiverPhone,
    receiverPostCode,
    receiverProvince,
    countryOptions,
    targetCity,
    receiverCity,
    receiverDetailAddress,
    deliveryType,
    signImageUrl,
    pickupTime,
  ]);

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center gap-2 mb-4 text-base font-medium">
        <User className="w-5 h-5" />
        <CopyButton copyText={recipientInformationText}>
          <Trans i18nKey={LOCALS.recipient_information} />
        </CopyButton>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-5">
        {infoList.map((item) => {
          return (
            <div key={item.key}>
              <div className="text-sm font-medium text-gray-500">
                {item.label}
              </div>
              <div className="mt-1 text-sm font-medium">{item.node}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default RecipientInformation;
